// eslint-disable-next-line check-file/filename-naming-convention
import { toFixedNumber } from '.';
import { Euler } from './Euler';
import { Matrix4 } from './Matrix4';
import { Quaternion } from './Quaternion';
import { Vector3 } from './Vector3';

type Transform = number[];

/**
 * 将变换矩阵转换为 Matrix4
 *
 * @param transform - 变换矩阵
 */
function toMatrix4(transform: Transform) {
    if (transform.length !== 6) {
        throw new Error(`invalid transform length`);
    }

    const matrix4 = new Matrix4();

    // prettier-ignore
    const [
    a, b,
    c, d,
    tx, ty
  ] = transform;

    // prettier-ignore
    matrix4.set(
    a,  b,  0, 0,
    c,  d,  0, 0,
    0,  0,  1, 0,
    tx, ty, 0, 1
  );

    return matrix4;
}

/**
 * 将四元数转换为弧度
 *
 * @param quaternion - 四元数
 */
function toRadian(quaternion: Quaternion) {
    const [, , z] = new Euler().setFromQuaternion(quaternion).toArray();

    return toFixedNumber((-180 * z) / Math.PI);
}

/**
 * 从变换矩阵中分解出弧度 / 缩放信息
 *
 * - 目前只支持 2D 矩阵 (即长度为 `6` 的数组)
 *
 * @param transform - 变换矩阵
 *
 * @example
 * ```js
 * // returns [{ radian: -177, xScale: 1.94, yScale: 3.14 }, Matrix4, Vector3, Quaternion, Vector3]
 * decomposeLayerTextTransform([-1.9344659046174852, -0.16422606909941967, 0.1013810621597626, -3.133620072174337, 454.1347977283658, 346.72489674918256])
 * ```
 */
export function decomposeLayerTextTransform(transform: Transform) {
    const matrix4 = toMatrix4(transform);
    const position = new Vector3();
    const quaternion = new Quaternion();
    const scale = new Vector3();

    matrix4.decompose(position, quaternion, scale);

    const radian = toRadian(quaternion);
    const xScale = toFixedNumber(scale.x);
    const yScale = toFixedNumber(scale.y);

    return [{ radian, xScale, yScale }, matrix4, position, quaternion, scale] as const;
}
